import {
  buildPermissionBusinessFieldRows,
  isDeviceMasterQuoteField,
  QUOTE_SOURCE_FIELDS,
} from './businessFieldDirectory';
import { customerSnapshotForQuote } from './customerBinding';
import { requireDeviceSku } from './deviceCatalog';
import {
  formatQuoteFieldValue,
  resolveQuoteFieldValue,
  resolveQuoteHeaderFieldDisplay,
  resolveQuoteSystemFieldDisplay,
} from './fieldValueResolver';
import {
  activeRoleIdForState,
  channelRequiredFieldGrant,
  quotePermissionContext,
  resolveDisposableChannelFieldIds,
  resolveRecursiveEnterpriseFieldGrant,
} from './permissionPolicy';
import { visibleQuoteColumnsForState } from './quoteColumns';
import { createPricingBaseSnapshot, lineTotal } from './quoteTotals';
import {
  effectiveShareFieldIdsForChannel,
  SHARE_PUBLIC_REQUIRED_FIELD_IDS,
} from './shareExportFieldCombinations';
import type {
  CpqState,
  FrozenCustomerSnapshot,
  PublicShareItemSnapshot,
  QuoteColumnSnapshot,
  QuoteItem,
  QuoteOutputBusinessSectionSnapshot,
  QuoteOutputProjectionSnapshot,
  ShareFieldCombinationChannel,
  ShareFieldCombinationRecord,
  ShareFieldCombinationSnapshot,
  ShareFieldDefinitionSnapshot,
} from './types';
import type { UnifiedFieldValuePort } from './unifiedFieldPort';
import {
  quotePlacementForOwner,
  unifiedFieldBindingForKey,
} from './unifiedFieldRegistry';

export type QuoteOutputChannelId = ShareFieldCombinationChannel;

export interface QuoteChannelProjectionColumn {
  fieldId: string;
  label: string;
  placement?: 'quote-line' | 'package-summary' | 'quote-header';
}

export interface QuoteChannelProjectionRow {
  values: Record<string, string | number | boolean | string[] | null>;
  rowType?: 'quote-line' | 'package-summary' | 'quote-header';
  scopeId?: string;
}

/** Explicit output DTO. It is derived from domain facts and policy; it is not
 * persisted as a second quote model. Publications freeze only its public form. */
export interface QuoteChannelProjection {
  channelId: QuoteOutputChannelId;
  quoteId: string;
  quoteVersion: number;
  fieldIds: string[];
  columns: QuoteChannelProjectionColumn[];
  rows: QuoteChannelProjectionRow[];
  customerSnapshot?: import('./types').FrozenCustomerSnapshot;
  validUntilSnapshot?: string;
  overallDiscountSnapshot?: number;
  publicItems: PublicShareItemSnapshot[];
  publicFieldSnapshot: QuoteColumnSnapshot[];
  selectionOptions: PublicShareItemSnapshot[];
  placementRows: import('./types').QuotePlacementValueSnapshot[];
}

export const PUBLIC_SHARE_REQUIRED_FIELDS = SHARE_PUBLIC_REQUIRED_FIELD_IDS;

export const PUBLIC_SHARE_SELECTION_FIELDS = [
  QUOTE_SOURCE_FIELDS.deviceId,
  QUOTE_SOURCE_FIELDS.sourceType,
  QUOTE_SOURCE_FIELDS.sourceId,
  QUOTE_SOURCE_FIELDS.sourceName,
  QUOTE_SOURCE_FIELDS.stock,
  QUOTE_SOURCE_FIELDS.replacementDeviceId,
  QUOTE_SOURCE_FIELDS.remark,
  'definition:device_name',
  'definition:device_sku',
  'definition:rental_price',
  'definition:tax_inclusive_plan_price',
  'definition:device_unit_price',
  'definition:device_compensation_price',
  'definition:warehouse_stock',
] as const;

function quoteFieldPlacement(
  state: CpqState,
  fieldKey: string,
): 'quote-line' | 'package-summary' | 'quote-header' {
  const definition = state.catalog.quoteFieldDefinitions.find(
    (field) => field.key === fieldKey,
  );
  const binding = unifiedFieldBindingForKey(
    state.catalog.quoteFieldDefinitions,
    fieldKey,
  );
  return (
    definition?.quotePlacement ??
    binding?.quotePlacement ??
    (binding ? quotePlacementForOwner(binding.owner, fieldKey) : undefined) ??
    'quote-line'
  );
}

function quoteSourceDisplayName(
  quote: CpqState['quotes'][string],
  item: QuoteItem,
) {
  const group = quote.quoteGroups?.find(
    (candidate) => candidate.id === item.quoteGroupId,
  );
  const expectedKind =
    item.sourceType === 'plan'
      ? 'plan'
      : item.sourceType === 'package'
        ? 'package'
        : undefined;
  if (!expectedKind) return item.sourceName;

  const itemContainer = quote.containerInstances?.find(
    (container) => container.id === item.containerInstanceId,
  );
  const containerId =
    expectedKind === 'plan'
      ? (item.localPlanInstanceId ??
        group?.planInstanceId ??
        (itemContainer?.kind === 'plan'
          ? itemContainer.id
          : itemContainer?.parentInstanceId))
      : (item.containerInstanceId ?? group?.containerInstanceId);
  const container = quote.containerInstances?.find(
    (candidate) =>
      candidate.id === containerId && candidate.kind === expectedKind,
  );
  return container?.displayName?.trim() || item.sourceName;
}

function itemValue(
  state: CpqState,
  quoteId: string,
  item: QuoteItem,
  fieldId: string,
  columnSnapshot: QuoteColumnSnapshot[],
  fieldValuePort: UnifiedFieldValuePort,
): string | number | boolean | string[] | null | undefined {
  const quote = state.quotes[quoteId];
  if (!quote) return undefined;
  const sourceValue = (() => {
    if (fieldId === QUOTE_SOURCE_FIELDS.deviceId) return item.deviceId;
    if (fieldId === QUOTE_SOURCE_FIELDS.name)
      return item.quoteDisplayName?.trim() || item.name;
    if (fieldId === QUOTE_SOURCE_FIELDS.sku) return item.sku;
    if (fieldId === QUOTE_SOURCE_FIELDS.sourceType) return item.sourceType;
    if (fieldId === QUOTE_SOURCE_FIELDS.sourceId) return item.sourceId;
    if (fieldId === QUOTE_SOURCE_FIELDS.sourceName)
      return quoteSourceDisplayName(quote, item);
    if (fieldId === QUOTE_SOURCE_FIELDS.quantity) return item.quantity;
    if (fieldId === QUOTE_SOURCE_FIELDS.discount) return item.discount;
    if (fieldId === QUOTE_SOURCE_FIELDS.stock) return item.stock;
    if (fieldId === QUOTE_SOURCE_FIELDS.replacementDeviceId)
      return item.replacementDeviceId;
    if (fieldId === QUOTE_SOURCE_FIELDS.remark) return item.remark;
    if (fieldId === QUOTE_SOURCE_FIELDS.lineTotal) return lineTotal(item);
    return undefined;
  })();
  if (sourceValue !== undefined) return sourceValue;
  if (!fieldId.startsWith('definition:')) return undefined;
  const key = fieldId.slice('definition:'.length);
  const field = columnSnapshot.find((column) => column.key === key);
  if (!field) return undefined;
  if ((field.placement ?? 'quote-line') !== 'quote-line') return undefined;
  const definition = state.catalog.quoteFieldDefinitions.find(
    (candidate) => candidate.key === key,
  );
  if (definition?.governance.kind === 'custom') {
    const resolved = resolveQuoteFieldValue(state, key, quote, {
      placement: 'quote-line',
      item,
      fieldValuePort,
    });
    return resolved.status === 'value' ? (resolved.value ?? null) : null;
  }
  const resolved = resolveQuoteFieldValue(state, key, quote, {
    placement: 'quote-line',
    item,
    fieldValuePort,
  });
  if (field.calculation)
    return resolved.status === 'value' ? (resolved.value ?? null) : null;
  const masterMaintained = new Set([
    'scheme_discount',
    'scheme_product_price',
    'scheme_product_subtotal',
    'package_item_price',
    'package_discount_quote_tax13',
  ]);
  return field.sourceKind === 'manual' &&
    !masterMaintained.has(key) &&
    !isDeviceMasterQuoteField(key)
    ? resolved.status === 'value'
      ? (resolved.value ?? null)
      : null
    : resolveQuoteSystemFieldDisplay(field, item, quote);
}

export function calculationDependenciesAllowed(
  state: CpqState,
  roleId: string,
  channelId: QuoteOutputChannelId,
  quote: CpqState['quotes'][string],
  fieldId: string,
  selected: ReadonlySet<string>,
  visiting = new Set<string>(),
): boolean {
  const grant = channelRequiredFieldGrant(channelId);
  if (!grant) return false;
  return resolveRecursiveEnterpriseFieldGrant(
    state,
    roleId,
    fieldId,
    grant,
    quote.status === 'draft' ? 'draft' : 'submitted',
    quotePermissionContext(state, quote),
    selected,
    visiting,
  );
}

function publicItem(
  quote: CpqState['quotes'][string],
  item: QuoteItem,
  fieldIds: ReadonlySet<string>,
  fieldValues: Record<string, string | number | boolean | string[] | null>,
): PublicShareItemSnapshot {
  const finalLineAmount = fieldIds.has(
    'definition:product_final_discount_price',
  )
    ? lineTotal(item)
    : 0;
  const customerUnitPrice =
    item.quantity > 0 ? finalLineAmount / item.quantity : 0;
  return {
    itemId: item.itemId,
    name: item.quoteDisplayName?.trim() || item.name,
    sku: item.sku,
    ...(fieldIds.has(QUOTE_SOURCE_FIELDS.image) &&
    item.deviceFieldSnapshot?.image
      ? { image: item.deviceFieldSnapshot.image }
      : {}),
    quantity: item.quantity,
    // The public DTO carries the already-approved customer-facing result, not
    // the internal rental/retail/buyout/package pricing basis. Keeping the
    // legacy shape preserves the 3.42 receipt UI without bypassing channels.
    unitPrice: customerUnitPrice,
    discount: 100,
    pricingBaseSnapshot: {
      kind: 'legacy-unclassified',
      amount: customerUnitPrice,
      calculationVersion: 'pricing-v2',
    },
    ...(fieldIds.has(QUOTE_SOURCE_FIELDS.deviceId)
      ? { deviceId: item.deviceId }
      : {}),
    ...(fieldIds.has(QUOTE_SOURCE_FIELDS.sourceType)
      ? { sourceType: item.sourceType }
      : {}),
    ...(fieldIds.has(QUOTE_SOURCE_FIELDS.sourceId)
      ? { sourceId: item.sourceId }
      : {}),
    ...(fieldIds.has(QUOTE_SOURCE_FIELDS.sourceName)
      ? {
          sourceName:
            typeof fieldValues[QUOTE_SOURCE_FIELDS.sourceName] === 'string'
              ? (fieldValues[QUOTE_SOURCE_FIELDS.sourceName] as string)
              : quoteSourceDisplayName(quote, item),
        }
      : {}),
    ...(fieldIds.has(QUOTE_SOURCE_FIELDS.stock) ? { stock: item.stock } : {}),
    ...(fieldIds.has(QUOTE_SOURCE_FIELDS.replacementDeviceId) &&
    item.replacementDeviceId
      ? { replacementDeviceId: item.replacementDeviceId }
      : {}),
    ...(fieldIds.has(QUOTE_SOURCE_FIELDS.remark) && item.remark
      ? { remark: item.remark }
      : {}),
    ...(Object.keys(fieldValues).length > 0 ? { fieldValues } : {}),
  };
}

export function buildQuoteChannelProjection(
  state: CpqState,
  quoteId: string,
  channelId: QuoteOutputChannelId,
  fieldValuePort: UnifiedFieldValuePort,
  selectedFieldIds?: readonly string[],
  customerSnapshotOverride?: FrozenCustomerSnapshot,
): QuoteChannelProjection {
  const quote = state.quotes[quoteId];
  const roleId = activeRoleIdForState(state);
  if (!quote || !roleId)
    throw new Error('报价或明确角色不存在，输出投影已拒绝');
  const channelFieldIds = resolveDisposableChannelFieldIds(
    state,
    roleId,
    channelId,
    quotePermissionContext(state, quote),
  );
  const resolvedFieldIds = selectedFieldIds
    ? effectiveShareFieldIdsForChannel(selectedFieldIds, channelId)
    : channelFieldIds;
  if (
    selectedFieldIds &&
    resolvedFieldIds.some((fieldId) => !channelFieldIds.includes(fieldId))
  )
    throw new Error('选定分享字段未获当前输出通道批准');
  const selectedBeforeDependencyGate = new Set(resolvedFieldIds);
  const fieldIds = resolvedFieldIds.filter((fieldId) =>
    calculationDependenciesAllowed(
      state,
      roleId,
      channelId,
      quote,
      fieldId,
      selectedBeforeDependencyGate,
    ),
  );
  if (fieldIds.length === 0) throw new Error('输出通道或字段权限不足');
  if (
    channelId === 'channel:public-link' &&
    PUBLIC_SHARE_REQUIRED_FIELDS.some((fieldId) => !fieldIds.includes(fieldId))
  ) {
    throw new Error('公开链接通道缺少必需字段');
  }
  const allowed = new Set(fieldIds);
  const baseColumnSnapshot =
    quote.status !== 'draft'
      ? (quote.customColumnSnapshot ?? []).map((field) => ({
          ...field,
          placement: field.placement ?? quoteFieldPlacement(state, field.key),
        }))
      : (['quote-line', 'package-summary', 'quote-header'] as const).flatMap(
          (placement) =>
            visibleQuoteColumnsForState(state, quote, placement).map((field) =>
              field.placement === placement ? field : { ...field, placement },
            ),
        );
  const selectedDefinitionSnapshots = (selectedFieldIds ? resolvedFieldIds : [])
    .filter((fieldId) => fieldId.startsWith('definition:'))
    .flatMap((fieldId) => {
      const key = fieldId.slice('definition:'.length);
      const definition = state.catalog.quoteFieldDefinitions.find(
        (candidate) => candidate.key === key,
      );
      if (!definition) return [];
      return [
        {
          key,
          label: definition.label,
          type: definition.type,
          width: definition.width,
          note: definition.note,
          options: definition.options,
          sourceKind: definition.sourceKind,
          fieldKey: definition.key,
          definitionVersion: definition.governance.definitionVersion,
          formulaVersion: definition.governance.formulaVersion,
          calculation: definition.calculation,
          placement: quoteFieldPlacement(state, definition.key),
        } satisfies QuoteColumnSnapshot,
      ];
    });
  const columnSnapshot = [
    ...baseColumnSnapshot,
    ...selectedDefinitionSnapshots.filter(
      (selected) =>
        !baseColumnSnapshot.some((existing) => existing.key === selected.key),
    ),
  ];
  const directory = new Map(
    buildPermissionBusinessFieldRows(state.catalog.quoteFieldDefinitions).map(
      (row) => [row.id, row],
    ),
  );
  const itemSourceFields = new Set<string>([
    QUOTE_SOURCE_FIELDS.deviceId,
    QUOTE_SOURCE_FIELDS.name,
    QUOTE_SOURCE_FIELDS.sku,
    QUOTE_SOURCE_FIELDS.sourceType,
    QUOTE_SOURCE_FIELDS.sourceId,
    QUOTE_SOURCE_FIELDS.sourceName,
    QUOTE_SOURCE_FIELDS.quantity,
    QUOTE_SOURCE_FIELDS.discount,
    QUOTE_SOURCE_FIELDS.stock,
    QUOTE_SOURCE_FIELDS.replacementDeviceId,
    QUOTE_SOURCE_FIELDS.remark,
    QUOTE_SOURCE_FIELDS.lineTotal,
  ]);
  const snapshotFieldIds = new Set(
    columnSnapshot.map((field) => `definition:${field.key}`),
  );
  const snapshotFields = new Map(
    columnSnapshot.map((field) => [`definition:${field.key}`, field]),
  );
  const columns = fieldIds.flatMap((fieldId) => {
    const row = directory.get(fieldId);
    if (
      !row ||
      (!itemSourceFields.has(fieldId) && !snapshotFieldIds.has(fieldId))
    ) {
      return [];
    }
    // Only a non-draft quote version has crossed the freeze boundary. Drafts
    // intentionally resolve labels and selected fields from the current pool;
    // an arbitrary draft customColumnSnapshot must not rename another field
    // identity or hide a newly selected custom field.
    return [
      {
        fieldId,
        label: snapshotFields.get(fieldId)?.label ?? row.label,
        placement: snapshotFields.get(fieldId)?.placement ?? 'quote-line',
      },
    ];
  });
  if (
    selectedFieldIds &&
    resolvedFieldIds.some(
      (fieldId) => !columns.some((column) => column.fieldId === fieldId),
    )
  )
    throw new Error('选定分享字段未绑定当前报价的可输出位置');
  const lineRows: QuoteChannelProjectionRow[] = quote.items.map((item) => ({
    rowType: 'quote-line',
    scopeId: item.itemId,
    values: Object.fromEntries(
      columns.flatMap(({ fieldId }) => {
        const value = itemValue(
          state,
          quoteId,
          item,
          fieldId,
          columnSnapshot,
          fieldValuePort,
        );
        return value === undefined ? [] : [[fieldId, value]];
      }),
    ),
  }));
  const packageGroups = new Map<string, QuoteItem[]>();
  for (const item of quote.items)
    if (item.packageId)
      packageGroups.set(item.packageId, [
        ...(packageGroups.get(item.packageId) ?? []),
        item,
      ]);
  const packageRows: QuoteChannelProjectionRow[] = [
    ...packageGroups.entries(),
  ].map(([packageId, items]) => ({
    rowType: 'package-summary',
    scopeId: packageId,
    values: Object.fromEntries(
      columns.flatMap((column) => {
        if (column.placement !== 'package-summary') return [];
        const field = snapshotFields.get(column.fieldId);
        if (!field) return [];
        const value =
          field.key === 'package_item_price'
            ? items
                .map(
                  (item) =>
                    `${item.name} ${resolveQuoteSystemFieldDisplay(field, item, quote)}`,
                )
                .join('；')
            : field.sourceKind === 'manual'
              ? (() => {
                  const definition = state.catalog.quoteFieldDefinitions.find(
                    (candidate) => candidate.key === field.key,
                  );
                  if (definition?.governance.kind === 'custom')
                    return formatQuoteFieldValue(
                      field,
                      resolveQuoteFieldValue(state, field.key, quote, {
                        placement: 'package-summary',
                        item: items[0],
                        fieldValuePort,
                      }),
                    );
                  return resolveQuoteSystemFieldDisplay(field, items[0], quote);
                })()
              : resolveQuoteSystemFieldDisplay(field, items[0], quote);
        return [[column.fieldId, value]];
      }),
    ),
  }));
  const headerValues = Object.fromEntries(
    columns.flatMap((column) => {
      if (column.placement !== 'quote-header') return [];
      const field = snapshotFields.get(column.fieldId);
      if (!field) return [];
      const definition = state.catalog.quoteFieldDefinitions.find(
        (candidate) => candidate.key === field.key,
      );
      return [
        [
          column.fieldId,
          definition?.governance.kind === 'custom'
            ? formatQuoteFieldValue(
                field,
                resolveQuoteFieldValue(state, field.key, quote, {
                  placement: 'quote-header',
                  fieldValuePort,
                }),
              )
            : resolveQuoteHeaderFieldDisplay(
                field,
                quote,
                state.catalog.businessModes,
              ),
        ],
      ];
    }),
  );
  const rows = [
    ...lineRows,
    ...packageRows,
    ...(Object.keys(headerValues).length
      ? [
          {
            rowType: 'quote-header' as const,
            scopeId: quote.quoteId,
            values: headerValues,
          },
        ]
      : []),
  ];
  const isPublicLink = channelId === 'channel:public-link';
  const customerSnapshot = isPublicLink
    ? (customerSnapshotOverride ?? customerSnapshotForQuote(state, quote))
    : undefined;
  const publicFieldSnapshot = isPublicLink
    ? columnSnapshot.filter((field) => allowed.has(`definition:${field.key}`))
    : [];
  const publicItems = isPublicLink
    ? quote.items.map((item, index) => {
        const fieldValues = Object.fromEntries(
          publicFieldSnapshot
            .filter(
              (field) =>
                `definition:${field.key}` !== QUOTE_SOURCE_FIELDS.image,
            )
            .map((field) => [
              `definition:${field.key}`,
              rows[index].values[`definition:${field.key}`] ?? null,
            ]),
        );
        return publicItem(quote, item, allowed, fieldValues);
      })
    : [];
  const selectionOptions =
    isPublicLink &&
    PUBLIC_SHARE_SELECTION_FIELDS.every((fieldId) =>
      channelFieldIds.includes(fieldId),
    )
      ? state.catalog.devices
          .filter(
            (device) =>
              device.status === 'active' &&
              Boolean(device.sku?.trim()) &&
              Number.isFinite(
                quote.businessModeCode === 'buyout'
                  ? device.taxInclusivePlanPrice
                  : quote.businessModeCode === 'rental' ||
                      quote.businessModeCode === 'short_rental'
                    ? device.rentalPrice
                    : device.retailPriceTax13,
              ),
          )
          .map((device) => {
            const pricingBaseSnapshot = createPricingBaseSnapshot(
              quote.businessModeCode,
              {
                rentalPrice: device.rentalPrice,
                retailPriceTax13: device.retailPriceTax13,
                taxInclusivePlanPrice: device.taxInclusivePlanPrice,
              },
            );
            return {
              itemId: `SELECTION-${device.id}`,
              deviceId: device.id,
              name: device.name,
              sku: requireDeviceSku(device),
              quantity: 1,
              unitPrice: pricingBaseSnapshot.amount,
              discount: 100,
              pricingBaseSnapshot,
              stock: Object.values(device.stockByWarehouse).reduce(
                (sum, value) => sum + value,
                0,
              ),
              sourceType: 'device' as const,
              sourceId: device.id,
              sourceName: device.name,
            };
          })
      : [];
  return {
    channelId,
    quoteId: quote.quoteId,
    quoteVersion: quote.version,
    fieldIds,
    columns,
    rows,
    ...(customerSnapshot
      ? {
          customerSnapshot,
          validUntilSnapshot: quote.validUntil,
          overallDiscountSnapshot: quote.overallDiscount,
        }
      : {}),
    publicItems,
    publicFieldSnapshot,
    selectionOptions,
    placementRows: rows.flatMap((row) =>
      row.rowType === 'package-summary' || row.rowType === 'quote-header'
        ? [
            {
              placement: row.rowType,
              scopeId: row.scopeId ?? '',
              values: structuredClone(row.values),
            },
          ]
        : [],
    ),
  };
}

/** Copy only the output facts needed by a local export task. */
export function quoteOutputProjectionSnapshot(
  projection: QuoteChannelProjection,
  businessSections?: QuoteOutputBusinessSectionSnapshot[],
): QuoteOutputProjectionSnapshot {
  return {
    channelId: projection.channelId,
    fieldIds: [...projection.fieldIds],
    columns: structuredClone(projection.columns),
    rows: structuredClone(projection.rows),
    ...(businessSections?.length
      ? { businessSections: structuredClone(businessSections) }
      : {}),
  };
}

/** Freeze combination metadata and current Registry definitions at use time. */
export function shareFieldCombinationSnapshot(
  state: CpqState,
  combination: ShareFieldCombinationRecord,
  projection: QuoteChannelProjection,
): ShareFieldCombinationSnapshot {
  const directory = new Map(
    buildPermissionBusinessFieldRows(state.catalog.quoteFieldDefinitions).map(
      (row) => [row.id, row],
    ),
  );
  const columns = new Map(
    projection.columns.map((column) => [column.fieldId, column]),
  );
  const fields: ShareFieldDefinitionSnapshot[] = projection.fieldIds.map(
    (fieldId) => {
      const row = directory.get(fieldId);
      const definition = row?.definitionKey
        ? state.catalog.quoteFieldDefinitions.find(
            (candidate) => candidate.key === row.definitionKey,
          )
        : undefined;
      const column = columns.get(fieldId);
      return {
        fieldId,
        label: column?.label ?? row?.label ?? fieldId,
        type: definition?.type ?? 'text',
        width: definition?.width ?? 'normal',
        sourceKind: definition?.sourceKind ?? 'system',
        ...(column?.placement ? { placement: column.placement } : {}),
        ...(definition?.governance.definitionVersion
          ? { definitionVersion: definition.governance.definitionVersion }
          : {}),
        ...(definition?.governance.formulaVersion
          ? { formulaVersion: definition.governance.formulaVersion }
          : {}),
      };
    },
  );
  return {
    id: combination.id,
    name: combination.name,
    ...(combination.fieldIds.length > 0
      ? { selectedFieldIds: [...combination.fieldIds] }
      : {}),
    fieldIds: [...projection.fieldIds],
    channels: [...combination.channels],
    fields,
  };
}
